import React, { ComponentProps as CP } from 'react'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core/styles'
import Jersey from 'components/Jersey'
import PlayerPosition from 'components/PlayerPosition'
import T from 'components/FormattedMessage'
import { IPlayer, Player, PlayerStatus } from 'protos'
import classNames from 'classnames'

type Props = Omit<CP<typeof ListItem>, 'button'> & {
  player: IPlayer
  selected?: boolean
  className?: string
  onJerseyClick: (player: IPlayer) => void
}

const styles = (theme: Theme) => createStyles({
  root: {
    position: 'relative'
  },
  pale: {
    opacity: .65
  },
  jerseyContainer: {
    position: 'relative'
  },
  playerInfo: {
    flex: 4,
  },
  title: {
    fontWeight: 500
  },
  notPlayingTitle: {
    color: theme.palette.error.main
  },
  notPlayingBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    zIndex: 1,
    width: 14,
    height: 14,
    borderRadius: '150%',
    backgroundColor: theme.palette.error.main,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.white,
  },
  playerAttribute: {
    flex: 1,
    textAlign: 'right',
  },
  selected: {
    backgroundColor: theme.palette.grey[50],

    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: 4,
      height: '100%',
      backgroundColor: theme.palette.primary.main
    }
  },
  points: {
    opacity: .5,
  },
  rightPad: {
    paddingRight: 2,
  },
  costValue: {
    fontSize: theme.typography.subtitle1.fontSize
  },
  playerName: {
    marginRight: 3
  }
})

const getMissingReasonMessageKey = (
  missingReason: IPlayer['missingReason']
): string => ({
  default: '',
  [Player.MissingReason.PMR_NONE]: '',
  [Player.MissingReason.PMR_OTHER]: '',
  [Player.MissingReason.PMR_INJURY]: 'playerstatus_injured_short',
  [Player.MissingReason.PMR_SUSPENSION]: 'playerstatus_suspended_short',
}[missingReason || 'default'])

const getPlayerStatusMessageKey = (
  status: IPlayer['status']
): string => ({
  default: '',
  [PlayerStatus.PS_UNKNOWN]: '',
  [PlayerStatus.PS_ACTIVE]: '',
  [PlayerStatus.PS_FINISHED]: '',
  [PlayerStatus.PS_INACTIVE]: 'playerstatus_inactive_short',
  [PlayerStatus.PS_LEFTOUT]: 'playerstatus_leftout_short',
  [PlayerStatus.PS_SENTOFF]: 'playerstatus_sentoff_short'
}[status || 'default'])

type InjectedProps = Props & WithStyles<typeof styles>

class PlayerListItem extends React.Component<InjectedProps, {}> {
  handleJerseyClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    this.props.onJerseyClick(this.props.player)
  }

  render() {
    const { player, selected, classes, className, onJerseyClick, ...props } = this.props

    const isNotPlaying = !!player.notPlaying
    const statusMessageKey = getMissingReasonMessageKey(player.missingReason)
      || getPlayerStatusMessageKey(player.status)

    const isPale = isNotPlaying && !!player.missingReason

    return (
      <ListItem
        {...props}
        divider
        button
        className={classNames(className, {
          [classes.pale]: isPale,
          [classes.selected]: selected
        })}
      >
        <ListItemIcon className={classes.playerAttribute}>
          <div className={classes.jerseyContainer} onClick={this.handleJerseyClick}>
            {isNotPlaying && <div className={classes.notPlayingBadge} />}
            <Jersey size="small" jersey={player.jersey!} />
          </div>
        </ListItemIcon>
        <ListItemText
          primary={
            <>
              <span className={classes.playerName}>{player.name}</span>
              {statusMessageKey && <T id={statusMessageKey} />}
            </>
          }
          secondary={(
            <>
              (<PlayerPosition position={player.position} variant="short" />)&nbsp;
              {player.team!.name}
            </>
          )}
          primaryTypographyProps={{
            className: classNames(classes.title, {
              [classes.notPlayingTitle]: isNotPlaying
            })
          }}
          secondaryTypographyProps={{
            variant: 'caption',
            display: 'block',
          }}
          className={classes.playerInfo}
        />
        <ListItemText
          primary={(
            <>
              <div>
                <span className={classes.rightPad}>
                  {Math.round(player.averageScore || 0)}
                </span>
                <T id="common_unit_pointpergame" />
              </div>
              <div>
                <span className={classes.rightPad}>
                  {Math.round(player.averageMinutesPlayed || 0)}
                </span>
                <T id="common_unit_averageminutes" />
              </div>
            </>
          )}
          className={classNames(classes.playerAttribute, classes.points)}
          primaryTypographyProps={{ variant: 'caption' }}
        />
        <ListItemText
          primary={(
            <>
              <span
                className={classNames(classes.rightPad, classes.costValue)}
              >
                {player.cost}
              </span>
              <T id="common_unit_budget" />
            </>
          )}
          className={classes.playerAttribute}
          primaryTypographyProps={{ variant: 'caption' }}
        />
      </ListItem>
    )
  }
}

export default withStyles(styles)(PlayerListItem)
